<template>
  <div
    class="rounded-lg cursor-pointer"
    :class="
      isHoveringDelete ? 'border-2 border-red-500' : 'border-2 border-[#D7DFEA]'
    "
    :style="!props.isReadonly ? { backgroundColor: props.color } : {}">
    <slot v-if="hasSlot('format')" name="format">
      <PairingFunctionOptions
        :format="props.format"
        :color="props.color"
        :can-delete="props.canDelete"
        @update:format="handleFormatUpdate"
        @delete:item="handleDeleteItem"
        @delete:hover="handleHoverDelete"/>
    </slot>
    <div
      class="flex shrink items-center justify-start relative rounded-lg border-3 bg-white transition-all duration-300 h-[125px] w-[180px]"
      :class="
        props.format === PairingItemFormat.ImageText
          ? 'flex-col'
          : 'flex-col-reverse'
      "
      :style="{
        borderColor: !props.isReadonly || active ? props.color : '#ffffff',
      }">
      <div
        class="absolute top-1 right-1 h-4 w-4 rounded-full z-10"
        :style="
          props.isReadonly && active
            ? { backgroundColor: props.color, border: '2px solid white' }
            : {}
        ">
        <div
          class="absolute rounded-full w-[35%] h-[35%] top-[15%] right-[15%]"
          :class="props.isReadonly && active ? 'bg-white' : ''"></div>
      </div>
      <slot
        v-if="hasSlot('uploadImage') && format.includes('image')"
        name="uploadImage">
        <div
          class="w-full"
          :class="`${format.includes('text') ? 'h-[70%]' : 'h-full'}`">
          <PairingUploadImage
            :id="props.id"
            v-model="imageUrl"
            :format="props.format"/>
        </div>
      </slot>
      <slot
        v-if="hasSlot('headline') && format.includes('text')"
        name="headline"
        :title="props.title"
        :format="props.format">
        <div
          ref="scrollBox"
          :style="`--scroll-color: ${color}`"
          class="bg-white w-full overflow-x-hidden custom-scroll"
          :class="{
            [`absolute p-1 pb-0 ${format.startsWith('image') ? 'bottom-0' : 'top-1'} max-h-[75px] has-image`]:
              format.includes('image'),
            'h-full flex items-center justify-center w-full my-1 p-1':
              !format.includes('image'),

            '-me-1': hasVerticalScroll,
          }">
          <HeadLine
            :id="`item-${id}-${isReadonly}`"
            v-model="title"
            v-tooltip.top="
              countUnitsInHtml(title) > maxLength
                ? {
                    value: parseTooltip(title, {
                      fontSize: '12px',
                      inverted: true,
                    }),
                    escape: false,
                  }
                : null
            "
            :closable="false"
            :placeholder="
              $t('blocksSettings.custom_video.text_area_placeholder')
            "
            visual-boundary="none"
            :options="{
              maxLength: maxLength,
              defaultNode: 'headline',
              sizeBreakpoint: 15,
              textAlign: 'center',
              fontSize: '16px',
            }"
            @blur="checkScroll"/>
        </div>
      </slot>
      <slot
        v-if="hasSlot('image') && format.includes('image')"
        name="image"
        :image="props.image"
        :format="props.format"
        :color="color ?? '#F3F7FC'"></slot>
      <slot
        v-if="hasSlot('title') && format.includes('text')"
        name="title"
        :title="props.title"
        :format="props.format"
        :color="color ?? '#F3F7FC'"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  type PropType,
  useSlots,
  computed,
  onMounted,
  nextTick,
} from 'vue';
import { PairingItemFormat } from '@amit/types';
import { HeadLine } from '@amit/components';
import PairingFunctionOptions from '../../../../products/builder/src/blocks/Pairing/PairingFunctionOptions.vue';
import PairingUploadImage from '../../../../products/builder/src/blocks/Pairing/PairingUploadImage.vue';
import { parseTooltip, countUnitsInHtml } from '@amit/tiptap';
import { useVModel } from '@vueuse/core';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  pairIndex: {
    type: Number,
    required: false,
  },
  format: {
    type: String as PropType<PairingItemFormat>,
    default: PairingItemFormat.TextImage,
  },
  title: {
    type: String,
    default: '',
  },
  image: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '',
  },
  matches: {
    type: Array<String>,
    default: [''],
  },
  active: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
  isHoveringDelete: {
    type: Boolean,
    default: false,
  },
});

const imageUrl = defineModel<string>({ default: '' });
const slots = useSlots();
const hasSlot = name => !!slots[name];

const emit = defineEmits<{
  'update:format': [format: PairingItemFormat];
  'update:title': [title: string];
  'delete:item': [id: string];
  'delete:hover': [hovering: boolean];
}>();

const handleFormatUpdate = (format: PairingItemFormat) => {
  emit('update:format', format);
};

const handleDeleteItem = () => {
  emit('delete:item', props.id);
};

const handleHoverDelete = (hovering: boolean) => {
  if (props.canDelete) {
    emit('delete:hover', hovering);
  }
};
const title = useVModel(props, 'title');

const maxLength = computed(() => (props.format.includes('image') ? 17 : 95));

const scrollBox = ref();
const hasVerticalScroll = ref(false);

const checkScroll = () => {
  const el = scrollBox.value as HTMLElement;
  if (!el) return;
  nextTick(() => {
    const katexEl = el.querySelector('.katex-string');
    if (katexEl) {
      hasVerticalScroll.value =
        el.scrollHeight > (props.format.includes('image') ? 75 : 110);
    } else {
      hasVerticalScroll.value = false;
    }
  });
};

onMounted(() => {
  nextTick(checkScroll);
  window.addEventListener('resize', checkScroll);
});
</script>

<style>
.custom-scroll {
  overflow-y: auto; /* or scroll */
  max-height: 300px; /* example */
}

/* Scrollbar track */
.custom-scroll::-webkit-scrollbar {
  width: 2px;
}

/* Scrollbar thumb */
.custom-scroll::-webkit-scrollbar-thumb {
  background-color: color-mix(in srgb, var(--scroll-color), black 30%);
  border-radius: 4px;
}

/* Scrollbar on hover */
.custom-scroll::-webkit-scrollbar-thumb:hover {
  background-color: color-mix(in srgb, var(--scroll-color), black 40%);
}

/* Optional: Scrollbar track background */
.custom-scroll::-webkit-scrollbar-track {
  background-color: var(--scroll-color);
  border-radius: 5px;
}
</style>

<style scoped>
:deep(.hover-menu) {
  @apply scale-[0.7];
}

.has-image :deep(.headline) .long-text:not(:has(.katex-string)),
.has-image :deep(.headline) .readonly-html label {
  overflow: hidden !important;
  height: 20px;
  @apply leading-[1.4];
}
</style>
